import { useState, useEffect, createContext, useContext, useMemo } from 'react';
import { set, get, getKeys, remove } from 'globals/storage';
import { getRegionalApiEndpoints } from 'globals/regional-api';
import { isUnifiedLoginEnabled } from 'globals/testable';
import { AuthClient, AppLogger, LogTypes } from '@workivate/tho-web-shared';
import hermesVersion from 'version';

import { moveChildKeys } from './env-controller.helpers';

// @ts-ignore (ignoring no types/module warning for now)
import environmentVariables from '../../../../environment-variables.yml';

type EnvControllerContextValue = {
  selectedEnvironment: string | null;
  setSelectedEnvironment: (env: string | null) => void;
};

export const EnvControllerContext = createContext<EnvControllerContextValue>({
  selectedEnvironment: null,
  setSelectedEnvironment: () => null,
});

export const useEnvControllerContext = () => useContext(EnvControllerContext);

export const setStoredSelectedEnvironment = (env: string) =>
  set<string>(getKeys().setEnvironment, env, true);

export const getStoredSelectedEnvironment = () =>
  get<string>(getKeys().setEnvironment, true);

export const removeStoredSelectedEnvironment = () =>
  remove(getKeys().setEnvironment);

export const setAPIEndpoints = (env: string | null) => {
  if (!env) return;

  const chosenEnvObject = environmentVariables[env];
  const resultEnvObject = moveChildKeys(chosenEnvObject);

  window.WAM.ENV = {
    ...window.WAM.ENV,
    ...(resultEnvObject as Window['WAM']['ENV']),
    ...getRegionalApiEndpoints(),
  };

  if (isUnifiedLoginEnabled()) {
    // reconfigure AuthClient with the new authEndpoint
    try {
      // If we import Session instead of using require, Webpack will try to resolve it at build time
      // leading to an error because app store is not ready yet
      const Session = require('state/session/helpers');

      if (AuthClient.authEndpoint) {
        AuthClient.configure({
          authEndpoint: `https://${window.WAM.ENV.awsCognitoSSODomain}`,
          redirectUri: Session.getOauth2RedirectUri(),
          environment: Session.getEnvironmentForHeader(),
          appName: 'web',
          appVersion: hermesVersion.version,
          // we deliberately do not reset persistence here
        });
      }
    } catch (error) {
      AppLogger.logWithOptions({
        message: 'Error re-configuring AuthClient ***',
        level: LogTypes.Error,
        error: error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
    }
  }

  setStoredSelectedEnvironment(env);
};

export const EnvControllerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const inNonViableEnvironment = IS_PRODUCTION || IS_TEST;

  const selectionFromStorage = getStoredSelectedEnvironment();

  const [selectedEnvironment, setSelectedEnvironment] = useState<string | null>(
    selectionFromStorage ?? null,
  );

  useEffect(() => {
    if (!selectedEnvironment || inNonViableEnvironment) return;

    setAPIEndpoints(selectedEnvironment);
  }, [selectedEnvironment]);

  const envContext: EnvControllerContextValue = useMemo(
    () => ({
      selectedEnvironment,
      setSelectedEnvironment,
    }),
    [selectedEnvironment],
  );

  if (inNonViableEnvironment) {
    return children;
  }

  return (
    <EnvControllerContext.Provider value={envContext}>
      {children}
    </EnvControllerContext.Provider>
  );
};
